import React, { useCallback, useState } from "react";
import { inject, observer } from "mobx-react";
import { useIntl } from "react-intl";
import BroadcastStore from "../_stores/broadcast/BroadcastStore";
import BroadcastStreamStore from "../_stores/broadcast-stream/BroadcastStreamStore";
import PricingStore from "../_stores/pricing/PricingStore";
import BroadcastStrategy from "../_stores/BroadcastStrategy";
import TailwindFlex from "library/components/_tailwind/flex";
import TailwindBox from "library/components/_tailwind/box";
import TailwindIcon from "library/components/_tailwind/icon";
import TailwindText from "library/components/_tailwind/text";
import { CustomIconName } from "library/components/_tailwind/icon/icons/enums";

interface MobileBroadcastQuickActionsProps {
  broadcastStore?: BroadcastStore;
  broadcastStreamStore?: BroadcastStreamStore;
  pricingStore?: PricingStore;
  broadcastStrategy?: BroadcastStrategy;
}

const MobileBroadcastQuickActions: React.FC<MobileBroadcastQuickActionsProps> =
  ({
    broadcastStore,
    broadcastStreamStore,
    pricingStore,
    broadcastStrategy,
  }) => {
    const intl = useIntl();
    const { isShowStarted, showTippingSum, isInTippingShow } = broadcastStore!;
    const {
      isMicrophoneMuted,
      setIsMicrophoneMuted,
      toggleMobileFacingMode,
      mobileFacingMode,
    } = broadcastStreamStore!;
    const { modelProducts, submitBuzzPriceDebounced } = pricingStore!;
    const { disconnect } = broadcastStrategy!;

    const [buzzEnabled, setBuzzEnabled] = useState(
      modelProducts?.buzz_status === "on"
    );

    const handleToggleCamera = useCallback(async () => {
      await toggleMobileFacingMode();
    }, [toggleMobileFacingMode]);

    const handleToggleMic = useCallback(() => {
      setIsMicrophoneMuted(!isMicrophoneMuted);
    }, [isMicrophoneMuted, setIsMicrophoneMuted]);

    const handleToggleBuzz = useCallback(() => {
      const newStatus = !buzzEnabled;
      setBuzzEnabled(newStatus);
      submitBuzzPriceDebounced(newStatus);
    }, [buzzEnabled, submitBuzzPriceDebounced]);

    const handleStopBroadcast = useCallback(() => {
      disconnect();
    }, [disconnect]);

    const actionButtonStyle = {
      width: 44,
      height: 44,
      borderRadius: "50%",
      background: "rgba(255, 255, 255, 0.1)",
      backdropFilter: "blur(12px)",
      WebkitBackdropFilter: "blur(12px)",
      border: "1px solid rgba(255, 255, 255, 0.18)",
      cursor: "pointer",
      transition: "all 0.2s ease",
    };

    const activeButtonStyle = {
      ...actionButtonStyle,
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      boxShadow: "0 2px 8px rgba(102, 126, 234, 0.4)",
    };

    const stopButtonStyle = {
      ...actionButtonStyle,
      background: "#ff3b5c",
      boxShadow: "0 2px 8px rgba(255, 59, 92, 0.4)",
    };

    return (
      <TailwindFlex
        position='absolute'
        inset={["top-14", "right-3"]}
        width='w-auto'
        flexDirection='flex-col'
        gap='gap-3'
        zIndex='z-10'>
        {/* Quick action buttons */}
        <TailwindFlex width='w-auto' flexDirection='flex-col' gap='gap-2.5'>
          {/* Flip camera (front/back) */}
          <TailwindBox
            as='button'
            display='flex'
            alignItems='items-center'
            justifyContent='justify-center'
            textColor='text-white'
            onClick={handleToggleCamera}
            style={actionButtonStyle}
            htmlElementProps={{
              title:
                mobileFacingMode === "user"
                  ? intl.formatMessage({
                      id: "broadcast.mobile.switchToBackCamera",
                      defaultMessage: "Switch to Back Camera",
                    })
                  : intl.formatMessage({
                      id: "broadcast.mobile.switchToFrontCamera",
                      defaultMessage: "Switch to Front Camera",
                    }),
            }}>
            <TailwindIcon name='flip_camera_ios' fontSize='text-xl' />
          </TailwindBox>

          {/* Mic mute/unmute */}
          <TailwindBox
            as='button'
            display='flex'
            alignItems='items-center'
            justifyContent='justify-center'
            textColor='text-white'
            onClick={handleToggleMic}
            style={actionButtonStyle}
            htmlElementProps={{
              title: isMicrophoneMuted
                ? intl.formatMessage({
                    id: "broadcast.mobile.unmuteMicrophone",
                    defaultMessage: "Unmute Microphone",
                  })
                : intl.formatMessage({
                    id: "broadcast.mobile.muteMicrophone",
                    defaultMessage: "Mute Microphone",
                  }),
            }}>
            <TailwindIcon
              name={isMicrophoneMuted ? "mic_off" : "mic"}
              fontSize='text-xl'
            />
          </TailwindBox>

          {/* Stop broadcast - only when streaming */}
          {isShowStarted && (
            <TailwindBox
              as='button'
              display='flex'
              alignItems='items-center'
              justifyContent='justify-center'
              onClick={handleStopBroadcast}
              style={stopButtonStyle}
              htmlElementProps={{
                title: intl.formatMessage({
                  id: "broadcast.mobile.stopBroadcast",
                  defaultMessage: "Stop Broadcast",
                }),
              }}>
              <TailwindBox
                width='w-4'
                height='h-4'
                backgroundColor='bg-white'
                borderRadius='rounded-sm'
              />
            </TailwindBox>
          )}

          {/* Buzz toggle - only when streaming */}
          {isShowStarted && (
            <TailwindBox
              as='button'
              display='flex'
              alignItems='items-center'
              justifyContent='justify-center'
              textColor='text-white'
              onClick={handleToggleBuzz}
              style={buzzEnabled ? activeButtonStyle : actionButtonStyle}
              htmlElementProps={{
                title: intl.formatMessage({
                  id: "broadcast.mobile.buzzMode",
                  defaultMessage: "Buzz Mode",
                }),
              }}>
              <TailwindIcon
                name={CustomIconName.notificationBuzz}
                fontSize='text-xl'
              />
            </TailwindBox>
          )}

          {/* Tipping goal counter */}
          {isShowStarted && isInTippingShow && (
            <TailwindBox
              display='flex'
              alignItems='items-center'
              justifyContent='justify-center'
              flexDirection='flex-col'
              textColor='text-white'
              style={{
                ...actionButtonStyle,
                cursor: "default",
              }}>
              <TailwindFlex
                alignItems='items-center'
                justifyContent='justify-center'
                gap='gap-0.5'>
                <TailwindText
                  fontWeight='font-bold'
                  textColor='text-yellow-500'
                  fontSize='text-xs'>
                  {showTippingSum}
                </TailwindText>
                <TailwindIcon
                  textColor='text-yellow-500'
                  name={CustomIconName.token}
                  style={{ fontSize: 10 }}
                />
              </TailwindFlex>
              <TailwindText
                textColor='text-gray-400'
                style={{ fontSize: 8, lineHeight: "10px" }}>
                /{modelProducts?.chat_settings?.tipping?.goal || 0}
              </TailwindText>
            </TailwindBox>
          )}

        </TailwindFlex>
      </TailwindFlex>
    );
  };

export default inject(
  "broadcastStore",
  "broadcastStreamStore",
  "pricingStore",
  "broadcastStrategy"
)(observer(MobileBroadcastQuickActions));
